import { Button } from '@/components/ui/button';
import { LucideIcon } from 'lucide-react';

export interface StatusBannerProps {
  variant: 'purple' | 'blue' | 'red' | 'amber';
  icon: LucideIcon;
  title: string;
  description: string;
  buttonText: string;
  buttonIcon?: LucideIcon;
  onButtonClick: () => void;
  secondaryButtonText?: string;
  secondaryButtonIcon?: LucideIcon;
  onSecondaryButtonClick?: () => void;
  hintText?: string;
  className?: string;
}

export function StatusBanner({
  variant,
  icon: Icon,
  title,
  description,
  buttonText,
  buttonIcon: ButtonIcon,
  onButtonClick,
  secondaryButtonText,
  secondaryButtonIcon: SecondaryButtonIcon,
  onSecondaryButtonClick,
  hintText,
  className = '',
}: StatusBannerProps) {
  const variantStyles = {
    purple: {
      container: 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-700',
      icon: 'text-purple-600 dark:text-purple-400',
      text: 'text-purple-800 dark:text-purple-200',
      hint: 'text-purple-700 dark:text-purple-300',
      button: 'bg-purple-600 hover:bg-purple-700 text-white border-0 font-medium',
    },
    blue: {
      container: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700',
      icon: 'text-blue-600 dark:text-blue-400',
      text: 'text-blue-800 dark:text-blue-200',
      hint: 'text-blue-700 dark:text-blue-300',
      button: 'bg-primary hover:bg-primary/90 text-primary-foreground border-0 font-medium',
    },
    red: {
      container: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
      icon: 'text-red-600 dark:text-red-400',
      text: 'text-red-800 dark:text-red-200',
      hint: 'text-red-700 dark:text-red-300',
      button: 'bg-red-600 hover:bg-red-700 text-white border-0 font-medium',
    },
    amber: {
      container: 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700',
      icon: 'text-amber-600 dark:text-amber-400',
      text: 'text-amber-800 dark:text-amber-200',
      hint: 'text-amber-700 dark:text-amber-300',
      button: 'border-amber-600 text-amber-700 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-400 font-medium',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={`border-b ${styles.container} ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Icon className={`h-5 w-5 ${styles.icon}`} />
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${styles.text}`}>
                <span className="font-semibold">{title}</span> — {description}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {hintText && (
              <span className={`text-xs ${styles.hint} hidden sm:block`}>
                {hintText} →
              </span>
            )}
            {secondaryButtonText && onSecondaryButtonClick && (
              <Button
                onClick={onSecondaryButtonClick}
                size="sm"
                variant="ghost"
                className={`${styles.hint} font-medium hidden sm:inline-flex`}
              >
                {SecondaryButtonIcon && <SecondaryButtonIcon className="h-3 w-3 mr-1.5" />}
                {secondaryButtonText}
              </Button>
            )}
            <Button
              onClick={onButtonClick}
              size="sm"
              variant={variant === 'amber' ? 'outline' : 'default'}
              className={styles.button}
            >
              {ButtonIcon && <ButtonIcon className="h-3 w-3 mr-1.5" />}
              {buttonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
